import type { Container, Sprite } from 'pixi.js';
import type { ClassDrawing, PaintedDrawing } from '../drawings';
import { type Shape, paintSheet } from '../paint';
import { type Look, fade } from '../player-look';
import type { Textures } from '../textures';
import { REFERENCE_RADIUS } from '../textures-looks';
import { add, hide, placeOutline, setTint } from '../util';
import {
  type DragonPath,
  type DragonView,
  type Point,
  createDragonView,
  hideDragon,
  placeDragon,
} from './ink-dragon';
import {
  BANDS,
  CREAM,
  KNOT,
  NEEDLE_TIP,
  TATOUE_PIECES,
  UNIT,
  inkColor,
  tattooColor,
} from './tatoue-paint';

type Parts = Readonly<Record<keyof typeof TATOUE_PIECES, Shape>>;

const EXTENT = 50;
const MIDDLE = 12;
const BOB_TICKS = 2;
const JAB = { reach: 1.4, ticks: 1.6 };
const TIP = { radius: 6, buzz: 0.5, calm: 0.7, flare: 0.6 };
const WAG = { most: 1.2, calm: 0.4 };
const INKED_TICKS = 4;
const BANDS_PER_ARM = BANDS.along.length;
const ORBIT = {
  dragons: 3,
  radius: 26,
  turnPerBeat: 0.17,
  spread: 2.1,
  step: 0.0315,
  size: 1 / 3.3,
};

function createTattooistLook(
  bodies: Container,
  glows: Container,
  textures: Textures,
  parts: Parts,
): Look {
  const arms = add(bodies, parts.arms);
  const bands = Array.from({ length: 2 * BANDS_PER_ARM }, () => add(bodies, parts.band));
  const outline = add(bodies, parts.silhouette);
  const apron = add(bodies, parts.apron);
  const needle = add(bodies, parts.needle);
  const gloves = add(bodies, parts.gloves);
  const tails = [add(bodies, parts.tail), add(bodies, parts.tail)];
  const bandana = add(bodies, parts.bandana);
  const dots = add(bodies, parts.dots);
  const tip = add(glows, textures.halo);
  const orbit: DragonView[] = Array.from({ length: ORBIT.dragons }, () =>
    createDragonView(bodies, glows, parts, textures.halo),
  );
  const body = [arms, ...bands, apron, needle, gloves, ...tails, bandana, dots];
  // -1 until a player's first image, whose tattoos never shine.
  let inked = -1;
  let inkedAt = Number.NEGATIVE_INFINITY;
  let originX = 0;
  let originY = 0;
  let cos = 1;
  let sin = 0;
  let size = 1;
  let facing = 0;

  function put(sprite: Sprite, along: number, across: number, turn = 0): void {
    sprite.position.set(
      originX + (along * cos - across * sin) * UNIT * size,
      originY + (along * sin + across * cos) * UNIT * size,
    );
    sprite.rotation = facing + turn;
  }

  let orbitTurn = 0;
  let orbitRadius = 0;
  let orbitX = 0;
  let orbitY = 0;
  let orbitIndex = 0;
  const orbitPath: DragonPath = (back: number, out: Point) => {
    const angle = orbitTurn + orbitIndex * ORBIT.spread - back * ORBIT.step;
    out.x = orbitX + Math.cos(angle) * orbitRadius;
    out.y = orbitY + Math.sin(angle) * orbitRadius;
  };

  return {
    reset() {
      inked = -1;
      inkedAt = Number.NEGATIVE_INFINITY;
    },
    hide() {
      hide(...body, outline, tip);
      orbit.forEach(hideDragon);
    },
    place(input) {
      const { player, x, y, angle, scale, color, frame } = input;
      const { now, calm, palette, light, tempo, camera } = frame;
      originX = x;
      originY = y;
      cos = Math.cos(angle);
      sin = Math.sin(angle);
      size = scale;
      facing = angle;

      const beats = now / tempo.ticksPerBeat;
      const bob = fade(input.sinceBeat, BOB_TICKS) * (calm ? 0.35 : 1);
      const jab = input.fireCount > 0 ? fade(input.sinceFire, JAB.ticks) : 0;
      for (const sprite of body) {
        sprite.visible = true;
        sprite.scale.set(scale);
      }

      put(arms, 0, 0);
      setTint(arms, input.teint);
      const { upgrades } = player;
      if (upgrades.length !== inked) {
        inkedAt = inked >= 0 && upgrades.length > inked ? now : inkedAt;
        inked = upgrades.length;
      }
      const shine = calm ? 0 : fade(now - inkedAt, INKED_TICKS);
      for (let index = 0; index < bands.length; index += 1) {
        const band = bands[index];
        if (band === undefined) {
          continue;
        }
        const side = index % 2 === 0 ? -1 : 1;
        const along = BANDS_PER_ARM - 1 - Math.floor(index / 2);
        const across = side * (BANDS.across - along * BANDS.slope);
        put(band, BANDS.along[along] ?? 0, across);
        const owned = upgradeOn(upgrades.length, index, bands.length);
        const newest = owned !== undefined && owned === upgrades.length - 1;
        band.scale.set(scale * (newest ? 1 + 0.35 * shine : 1));
        setTint(band, owned === undefined ? color : tattooColor(palette, upgrades[owned] ?? ''));
      }

      const wide = scale * (1 + 0.04 * bob);
      const short = scale * (1 - 0.03 * bob);
      put(apron, 0, 0);
      apron.scale.set(short, wide);
      put(outline, 0, 0);
      outline.scale.set(short, wide);
      placeOutline(outline, apron, parts.silhouette.texture, REFERENCE_RADIUS, frame);
      setTint(apron, 0xffffff);

      put(needle, JAB.reach * jab, 0);
      put(gloves, 0, 0);

      const wag = Math.sin((Math.PI * beats) / 2) * (calm ? WAG.calm : WAG.most);
      tails.forEach((tail, index) => {
        const side = index === 0 ? -1 : 1;
        put(
          tail,
          KNOT.along,
          0,
          Math.atan2(side * KNOT.spread + wag, -(KNOT.length - 0.5)) - Math.PI,
        );
        setTint(tail, color);
      });
      put(bandana, 0, 0);
      setTint(bandana, color);
      put(dots, 0, 0);
      setTint(dots, CREAM);

      const buzz = calm ? TIP.calm : TIP.buzz + (1 - TIP.buzz) * Math.exp(-5 * ((2 * beats) % 1));
      tip.visible = true;
      put(tip, NEEDLE_TIP.along + JAB.reach * jab, NEEDLE_TIP.across);
      tip.scale.set((TIP.radius * UNIT * scale * (1 + TIP.flare * jab)) / textures.halo.radius);
      tip.alpha = Math.min(1, light.haloAlpha * (buzz + jab));
      setTint(tip, color);

      if (!frame.portrait) {
        orbit.forEach(hideDragon);
        return 0;
      }
      const unit = UNIT * scale;
      const half = Math.min(camera.screenWidth, camera.screenHeight) / (2 * camera.scale);
      orbitRadius = Math.min(ORBIT.radius * unit, half - 4 * unit);
      orbitX = x + cos * MIDDLE * scale;
      orbitY = y + sin * MIDDLE * scale;
      orbitTurn = (calm ? 0.5 : 1) * beats * ORBIT.turnPerBeat;
      orbit.forEach((view, index) => {
        orbitIndex = index;
        placeDragon(
          view,
          orbitPath,
          Infinity,
          inkColor(palette, color, 2 * index),
          unit * ORBIT.size,
          1,
          light.haloAlpha,
          textures.halo,
        );
      });
      return 0;
    },
  };
}

function upgradeOn(count: number, band: number, bands: number): number | undefined {
  return band < count ? band + bands * Math.floor((count - 1 - band) / bands) : undefined;
}

export const TATOUE_DRAWING: ClassDrawing = {
  classId: 'tatoue',
  paint(density): PaintedDrawing {
    const { shapes, source } = paintSheet(TATOUE_PIECES, density);
    return {
      extent: EXTENT,
      height: 1,
      centerAhead: MIDDLE,
      shadow: shapes.shadow,
      source,
      create: (bodies: Container, glows: Container, textures: Textures) =>
        createTattooistLook(bodies, glows, textures, shapes),
    };
  },
};
